/**
 * src/lib/hooks/useAgentInvocationContext.js — Agent invocation context reader.
 *
 * Phase 82-02 — Chat System Unification, data foundation.
 *
 * Produces the seed scope the AgentShell uses to pre-populate chat scope chips
 * when the agent is opened from inside a project/deal/task/person view. This
 * is a mandatory CEO requirement: never re-type the context the agent was
 * opened from.
 *
 * How it works
 * - Listens to the existing `view-context-change` window event (the same bus
 *   AppShell already consumes). No new event contract.
 * - Accepts an `override` prop so AgentShell can force a context imperatively
 *   (used by the `open-agent-chat` event handler and deterministic tests).
 * - Resolves entity ids → rows via useAppDataContext lookups, so the seed
 *   entries arrive with resolved names (not empty placeholders).
 * - Auto-includes the parent project for task/deal contexts so the agent has
 *   full grounding without the CEO adding a second chip manually.
 *
 * Never writes to chat_conversations directly — consumers (AgentShell) pass
 * the seed into useChat's initialScope.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAppDataContext } from '../AppDataContext';
import { toScopeEntry, deriveCtx } from '../agent/scopeContract';

const EMPTY_DETAIL = {
  view: null,
  projectId: null,
  dealId: null,
  taskId: null,
  personId: null,
  projectName: null,
  dealName: null,
  taskName: null,
  personName: null,
};

/**
 * @param {object} [options]
 * @param {object} [options.override] — hard-coded context; when present the
 *   hook ignores the window event bus. Shape matches EMPTY_DETAIL.
 * @returns {{ ctx: string, seed: Array, view: string|null }}
 */
export function useAgentInvocationContext({ override } = {}) {
  const { projects = [], deals = [], tasks = [], people = [] } = useAppDataContext() || {};

  const [eventDetail, setEventDetail] = useState(EMPTY_DETAIL);

  useEffect(() => {
    if (override) return undefined;

    const handler = e => {
      const d = e?.detail || {};
      setEventDetail({
        view: d.view ?? null,
        projectId: d.projectId ?? null,
        dealId: d.dealId ?? null,
        taskId: d.taskId ?? null,
        personId: d.personId ?? null,
        projectName: d.projectName ?? null,
        dealName: d.dealName ?? null,
        taskName: d.taskName ?? null,
        personName: d.personName ?? null,
      });
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('view-context-change', handler);
      return () => window.removeEventListener('view-context-change', handler);
    }
    return undefined;
  }, [override]);

  const detail = override || eventDetail;

  return useMemo(() => {
    const seed = [];

    const byId = (arr, id) => (id ? arr.find(r => r && r.id === id) : null);

    if (detail.taskId) {
      const row = byId(tasks, detail.taskId) || {
        id: detail.taskId,
        task_name: detail.taskName || '',
      };
      seed.push(toScopeEntry(row, 'task'));

      const parentProjectId = row.project_id || detail.projectId;
      if (parentProjectId) {
        const projRow = byId(projects, parentProjectId) || {
          id: parentProjectId,
          project_name: detail.projectName || '',
        };
        seed.push(toScopeEntry(projRow, 'project'));
      }
    } else if (detail.dealId) {
      const row = byId(deals, detail.dealId) || {
        id: detail.dealId,
        deal_name: detail.dealName || '',
      };
      seed.push(toScopeEntry(row, 'deal'));

      const parentProjectId = row.project_id || detail.projectId;
      if (parentProjectId) {
        const projRow = byId(projects, parentProjectId) || {
          id: parentProjectId,
          project_name: detail.projectName || '',
        };
        seed.push(toScopeEntry(projRow, 'project'));
      }
    } else if (detail.projectId) {
      const row = byId(projects, detail.projectId) || {
        id: detail.projectId,
        project_name: detail.projectName || '',
      };
      seed.push(toScopeEntry(row, 'project'));
    } else if (detail.personId) {
      const row = byId(people, detail.personId) || {
        id: detail.personId,
        display_name: detail.personName || '',
      };
      seed.push(toScopeEntry(row, 'person'));
    }

    return {
      ctx: deriveCtx(seed),
      seed,
      view: detail.view || null,
    };
  }, [detail, projects, deals, tasks, people]);
}
